import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import Remarks from './Remarks';
import Remarks__list from './Remarks__list';
import { filterRemarksList } from '../../../../Custom/Custom';
import { refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';

function FinalCheckList() {
  const navigate = useNavigate();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  // State for checklist items
  const [checklistData, setChecklistData] = useState([]);

  // Use ref to track if data has been loaded to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  // Function to fetch checklist data from API
  const fetchChecklistData = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for fetching checklist data');
      dataLoadedRef.current = true;
      return;
    }

    console.log('Starting API call for AsiBalanceFinalChecklist with DSL:', dslNo, 'Year:', surveyYear);
    showLoading(true);
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        console.error('Authentication token not found for fetching checklist data');
        showLoading(false);
        dataLoadedRef.current = true;
        return;
      }

      // Extract only year part (e.g., "2024" from "2024-25")
      const yearOnly = surveyYear ? surveyYear.split('-')[0] : surveyYear;

      const payload = {
        dsl_no: dslNo,
        survey_year: yearOnly
      };

      console.log('Fetching checklist data with payload:', payload);

      const response = await axios.post(`${API_URL}api/input-items-indigenous/AsiBalanceFinalChecklist`, payload, currentConfig);

      if (response.data && response.data.status && Array.isArray(response.data.data)) {
        console.log('Checklist data fetched successfully:', response.data.data);

        // Map API data to checklist format
        const mappedChecklistData = response.data.data.map((item, index) => {
          // Convert observation string to boolean
          // "True" or "true" -> true, "False" or "false" -> false
          let observationValue = false;
          if (item.observation !== null && item.observation !== undefined) {
            const obsStr = String(item.observation).trim();
            // Handle both "True"/"False" (capitalized) and "true"/"false" (lowercase)
            observationValue = obsStr.toLowerCase() === 'true' || obsStr === '1';
          }

          return {
            id: item.id || index + 1,
            checkPoint: item.check_points || item.checkPoint || '',
            observation: observationValue
          };
        });

        setChecklistData(mappedChecklistData);

        // Map remarks data from API response if available
        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            // Format date for display
            let formattedDate = '';
            const dateValue = remark.createdDate || remark.created_date || remark.createdDate || remark.updated_date || remark.updated_time || remark.created_time || '';
            if (dateValue) {
              try {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                }
              } catch (e) {
                formattedDate = dateValue;
              }
            }

            return {
              slNo: index + 1,
              blocks: remark.block || '',
              columns: remark.columnsName || remark.columns_name || remark.columns || '',
              items: remark.items || remark.Items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: formattedDate || dateValue || '',
              remarksBy: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || '',
              role: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || ''
            };
          });
          setRemarksList(mappedRemarksList);
          console.log('Mapped remarks list:', mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        // Set default remarksData - Block to "ALL" by default to show all remarks
        setRemarksData({
          remarksBlock: (mappedRemarksList.length > 0) ? 'ALL' : '',
          remarksItems: '',
          remarks: ''
        });

        dataLoadedRef.current = true;
        console.log('Checklist data loaded successfully');
      } else {
        console.log('No checklist data found or invalid response');
        setChecklistData([]);
        setRemarksList([]);
        dataLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching checklist data from API:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 404) {
        console.log('No existing checklist data found, initializing with empty array');
        setChecklistData([]);
      } else {
        toast.error('Failed to load checklist data.', messages.COLOR_RED);
        setChecklistData([]);
      }
      dataLoadedRef.current = true;
    } finally {
      showLoading(false);
    }
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    const fetchHeaderData = async () => {
      // Prevent multiple API calls using refs
      if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
        console.log('Data already loaded, loading in progress, or API call already made - skipping');
        return;
      }

      try {
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);

          const headerInfo = {
            despatchSlNo: parsedData.dslNo || '',
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: parsedData.dslNo || '',
            surveyYear: parsedData.year || ''
          };

          setHeaderData(headerInfo);

          // Fetch checklist data if DSL No and Survey Year are available
          if (headerInfo.dslNo && headerInfo.surveyYear) {
            apiCallMadeRef.current = true; // Mark API call as made
            isLoadingRef.current = true;
            await fetchChecklistData(headerInfo.dslNo, headerInfo.surveyYear);
            isLoadingRef.current = false;
          } else {
            setChecklistData([]);
          }
        } else {
          console.log('No balanceSheet data found in localStorage');
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
          setChecklistData([]);
        }
      } catch (error) {
        console.error('Error fetching data from localStorage:', error);
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
        setChecklistData([]);
      }
    };

    fetchHeaderData();
  }, [fetchChecklistData]);

  // Handle checkbox change
  const handleCheckboxChange = (id) => {
    setChecklistData(prevData =>
      prevData.map(item =>
        item.id === id ? { ...item, observation: !item.observation } : item
      )
    );
  };

  // Get selected Item Descriptions from checklist for Remarks Items dropdown
  const selectedItemDescriptions = useMemo(() => {
    return checklistData.map(item => `Check Point ${item.id}: ${item.checkPoint.substring(0, 50)}...`).filter(item => item !== '');
  }, [checklistData]);

  // API configuration - using dynamic token with multiple fallbacks
  const getAuthConfig = () => {
    let token = localStorage.getItem('token') ||
        localStorage.getItem('jwt') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('userToken');

    if (!token) {
        token = sessionStorage.getItem('token') ||
            sessionStorage.getItem('jwt') ||
            sessionStorage.getItem('accessToken');
    }

    if (!token) {
        console.error('No token found in localStorage or sessionStorage');
        return null;
    }

    return {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
        }
    };
  };

  // Handle reset function
  const handleReset = () => {
    setChecklistData(prevData =>
      prevData.map(item => ({
        ...item,
        observation: false
      }))
    );
    setRemarksData({
      remarksBlock: '',
      remarksItems: '',
      remarks: ''
    });
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.dslNo || headerData.dslNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Extract year only from survey year (e.g., "2024-25" -> "2024")
      const yearOnly = headerData.surveyYear ? headerData.surveyYear.split('-')[0] : headerData.surveyYear || '2024';

      // Prepare checklist data for API
      const dataToSave = checklistData.map(item => ({
        dsl_no: headerData.dslNo,
        survey_year: yearOnly,
        check_point_id: item.id,
        check_point: item.checkPoint,
        observation: item.observation
      }));

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: headerData.dslNo.toString(),
          surveyYear: yearOnly,
          block: remarksData.remarksBlock || '',
          columnsName: remarksData.remarksItems || '',
          items: remarksData.remarksBlock || '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Saving Final Checklist data to API:', finalPayload);

      // TODO: Update API endpoint when available
      const response = await axios.post(`${API_URL}api/input-items-indigenous/AsiBalanceFinalChecklist`, finalPayload, currentConfig);

      if (response.data && response.data.status) {
        toast.success('Data saved successfully!', messages.COLOR_RED);
        // Refresh balance sheet validation to update header colors
        if (headerData.dslNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.dslNo, headerData.surveyYear);
        }
        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      {/* Header Section */}
      <HeaderInfo
        title="Final Checklist"
        icon="fa fa-check-circle"
        iconColor="#1976d2"
        titleColor="#1976d2"
        backgroundColor=""
        dispatchSlNo={headerData.despatchSlNo}
        permanentSlNo={headerData.permanentSlNo}
        companyName={headerData.companyName}
        companyPlaceholder="Enter Company Name"
        showBlockCType={false}
        includeTableStyles={false}
      />

      {/* Main Content Area */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <div className="table-responsive">
                <table className="table table-bordered mb-0">
                  <thead>
                    <tr>
                      <th>SL No.</th>
                      <th>Check Points</th>
                      <th>Observation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistData.map((item) => (
                      <tr key={item.id}>
                        <td style={{ textAlign: 'center', width: '80px' }}>{item.id}</td>
                        <td style={{ padding: '12px' }}>{item.checkPoint}</td>
                        <td style={{ textAlign: 'center', width: '120px' }}>
                          <input
                            type="checkbox"
                            checked={item.observation}
                            onChange={() => handleCheckboxChange(item.id)}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer'
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remarks Section */}
      <div className='row'>
        <div className='col-md-12 remarks-section'>
          <Remarks
            remarksData={remarksData}
            setRemarksData={setRemarksData}
            blockItemData={[]}
            filteredBlockItems={[]}
            isLoadingBlockItems={false}
            debouncedBlockItemSearch={() => { }}
            loadMoreBlockItemData={() => { }}
            hasMoreBlockItems={false}
            isLoadingMoreBlockItems={false}
            selectedItemsFromTable={selectedItemDescriptions}
            remarksList={remarksList}
            availableColumns={[1, 2, 3]}
          />
        </div>
      </div>


      {/* Action Buttons */}
      <div className="row">
        <div className="col-12">
          <hr />
        </div>
        <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
          <button
            type="button"
            className="btn btn_orange mx-1"
          >
            Print
            <i className="fa fa-print ms-1"></i>
          </button>
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={handleSaveToAPI}
          >
            Save
          </button>
          <button
            type="reset"
            className="btn btn_orange mx-1"
            onClick={handleReset}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={() => navigate('/asiScheduleMainMenu')}
          >
            Cancel
          </button>
        </div>
        <div className="col-md-4 text-center text-md-end">
          <button
            type="button"
            className="btn btn_orange mx-1"
            onClick={() => navigate('/part2')}
          >
            <i className="fa fa-angle-left me-1"></i>
            Previous
          </button>
         
        </div>
      </div>

      {/* Remarks List Section */}
      {(() => {
        const filteredRemarksList = filterRemarksList(remarksList, remarksData);
        return <div className=''><Remarks__list remarksData={filteredRemarksList} /></div>;
      })()}

      

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </div>
  );
}

export default FinalCheckList;